import { useMemo } from 'react';
import { Box, Skeleton } from '@mui/material';
import { useProductDetailsContext } from '@/components/content/ProductDetails/hooks/useProductDetailsContext';
import { useGetPDPSizes } from 'integration/hooks/useGetPDPSizes';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import valueIsNullOrUndef from 'integration/utils/typeAssertions/valueIsNullOrUndef';
import { ProductDetailsSizesGuideHeader } from '@/components/blocks/ProductDetails/SizesGuideModal/parts/Header';
import { Switch } from '@/utils/switch';
import { ProductDetailsShoesScenery } from '@/components/blocks/ProductDetails/SizesGuideModal/parts/ShoesScenery';
import { ProductDetailsClothesScenery } from '@/components/blocks/ProductDetails/SizesGuideModal/parts/ClothesScenery';
import { ProductDetailsSizesGuideAdaptiveDialog } from '@/components/blocks/ProductDetails/SizesGuideModal/parts/Dialog';
import { ProductDetailsSizeNotFound } from '@/components/blocks/ProductDetails/SizesGuideModal/parts/NotFound';
import { useDpDisclaimerSizeHandler } from '@/components/blocks/DpPdpDisclaimerSize';
import { useLockBodyScroll } from '@/utils/useLockBodyScroll';
import { ProductDetailsAccessoriesScenery } from '@/components/blocks/ProductDetails/SizesGuideModal/parts/AccessoriesScenery';

export enum PDP_SIZE_GUIDE_TYPE {
	SHOES = 'CALZADO',
	CLOTHES = 'ROPA',
	ACCESSORIES = 'ACCESORIOS',
	DEFAULT = 'DEFAULT',
}

const DEFAULT_FAMILY = 'UNISEX';

export const ProductDetailsSizesGuideDialog: React.FC = () => {
	const { data: sizesData, isLoading: loadingSizes } = useGetPDPSizes();
	const { product } = useProductDetailsContext();
	const { open, off } = useDpDisclaimerSizeHandler();

	useLockBodyScroll(open);

	const { data, gender, family } = useMemo(() => {
		const attributes = product?.attributes || [];

		const gender =
			findProductAttribute(attributes, 'Genero')?.value?.toUpperCase() || DEFAULT_FAMILY;

		const family =
			findProductAttribute(attributes, 'Familia')?.value?.toUpperCase() ||
			PDP_SIZE_GUIDE_TYPE.DEFAULT;

		if (family === PDP_SIZE_GUIDE_TYPE.ACCESSORIES) {
			const brand =
				findProductAttribute(attributes, 'Marca')?.value?.toUpperCase().replaceAll(' ', '_') || '';

			const data: any = getValueByPathFromUnknown(sizesData, [gender, family, brand]);

			return { data, gender, family };
		}

		if (!gender || !family || !sizesData) {
			return { data: undefined, gender, family };
		}

		const data: any = getValueByPathFromUnknown(sizesData, [gender, family]);

		return { data, gender, family };
	}, [sizesData, product]);

	return (
		<ProductDetailsSizesGuideAdaptiveDialog open={open} onClose={off}>
			<ProductDetailsSizesGuideHeader gender={gender} family={family} onClose={off} />
			<Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
				{loadingSizes ? (
					<Skeleton variant="rounded" sx={{ width: '100%', height: '100%' }} />
				) : valueIsNullOrUndef(data) ? (
					<ProductDetailsSizeNotFound />
				) : (
					<>
						{Switch(family)
							.case(PDP_SIZE_GUIDE_TYPE.SHOES, () => <ProductDetailsShoesScenery data={data} />)
							.case(PDP_SIZE_GUIDE_TYPE.CLOTHES, () => <ProductDetailsClothesScenery data={data} />)
							.case(PDP_SIZE_GUIDE_TYPE.ACCESSORIES, () => (
								<ProductDetailsAccessoriesScenery data={data} />
							))
							.defaultTo(() => (
								<ProductDetailsSizeNotFound />
							))}
					</>
				)}
			</Box>
		</ProductDetailsSizesGuideAdaptiveDialog>
	);
};
